import queryString, { ParsedQuery } from 'query-string';
import buildUrl from 'build-url';
import memoizeOne from 'memoize-one';
import { ListingPageUrlState, SortingParameters } from '../../redux/products/types';
import { PageType } from '../UrlManager/types';
import {
    convertUrlToFilters,
    convertQueryStringToFilters,
    convertFilterObjectToUrl,
    convertQueryFiltersToObject,
} from './filterUrlService';

const decodePageType = (pageTypeCandidate: string): PageType => {
    const knownPageTypes = Object.values(PageType) as PageType[];
    const pageType = knownPageTypes.find(type => type === pageTypeCandidate);
    return pageType || PageType.CATEGORY;
};

export const getPathParts = (path: string) => {
    const parts = path.split('/').filter(str => !!str);
    const pageTypeCandidate = parts.shift() || '';
    let listingPageKey = '';
    const listingPageType: PageType = decodePageType(pageTypeCandidate);
    const keysPageTypes = [PageType.LANDING, PageType.BRANDS, PageType.STYLES];

    if (keysPageTypes.includes(listingPageType)) {
        listingPageKey = parts.shift() || '';
    } else {
        listingPageKey = pageTypeCandidate;
    }

    const filtersPart = parts.join('/');

    return { listingPageType, listingPageKey, filtersPart };
};

const convertQueryToSortingParams = (query: ParsedQuery): SortingParameters => ({
    order: query.order as 'desc' | 'asc' | undefined,
    sort: query.sort as string | undefined,
});

const getCurrentPage = (queryObject: ParsedQuery): number => (queryObject.page ? Number(queryObject.page) : 1);

export const getQueryParameterValue = (queryObject: ParsedQuery, name: string): string | undefined => {
    if (queryObject[name]) {
        return queryObject[name] as string;
    }
    return undefined;
};

const convertUrlToListingPageStateImpl = (path: string, search: string): ListingPageUrlState => {
    const normalizedPath = path.endsWith('/') ? path : `${path}/`;
    const { filtersPart, listingPageKey, listingPageType } = getPathParts(normalizedPath);

    const pathFilters = convertUrlToFilters(filtersPart);
    const [queryFilters, queryToPathFilters] = convertQueryStringToFilters(search);
    const uniqueQueryToPathFilters = queryToPathFilters.filter(
        ({ name }) => !pathFilters.find(({ name: pathFilterName }) => name === pathFilterName)
    );

    const query = queryString.parse(search);
    const page = getCurrentPage(query);
    let searchKeyword = getQueryParameterValue(query, 'q') || getQueryParameterValue(query, 'searched');
    searchKeyword = searchKeyword ? searchKeyword.replace('+', ' ') : undefined;
    const strictSearch = getQueryParameterValue(query, 'strictSearch');

    const sortingParams = convertQueryToSortingParams(query);

    return {
        appliedFilters: [...pathFilters, ...queryFilters, ...uniqueQueryToPathFilters],
        page,
        listingPageKey,
        listingPageType,
        sortingParams,
        search: searchKeyword,
        strictSearch,
    };
};

const trimSlashes = (input: string = '') => input.replace(/^\/+|\/+$/g, '');

export const getPageTypeUrlPart = (state: ListingPageUrlState) => {
    switch (state.listingPageType) {
        case PageType.LANDING:
        case PageType.BRANDS:
        case PageType.STYLES:
            return `/${state.listingPageType}/${trimSlashes(state.listingPageKey)}`;
        case PageType.ALLPRODUCTS:
        case PageType.NEWPRODUCTS:
            return `/${state.listingPageType}`;
        default:
            return `/${trimSlashes(state.listingPageKey)}`;
    }
};

export const hasNoQueryParmaDataInProperties = (queryObject: object) =>
    Object.keys(queryObject).every(key => queryObject[key] === undefined);

export const convertListingPageStateToUrl = (listingPageState: ListingPageUrlState, host: string = '') => {
    const filtersUrlPart = convertFilterObjectToUrl(listingPageState.appliedFilters);
    const filtersQuery = convertQueryFiltersToObject(listingPageState.appliedFilters);
    const pageTypePart = getPageTypeUrlPart(listingPageState).toLowerCase();
    const queryParams: any = {
        ...filtersQuery,
        ...listingPageState.sortingParams,
        page: listingPageState.page > 1 ? listingPageState.page : undefined,
        q: listingPageState.search,
    };
    const path = encodeURI(`/${trimSlashes(`${pageTypePart}${filtersUrlPart}`)}/`);

    const passQueryParam = !hasNoQueryParmaDataInProperties(queryParams);
    return buildUrl(host, { path, queryParams: passQueryParam ? queryParams : undefined });
};

export const convertUrlToListingPageState = memoizeOne(convertUrlToListingPageStateImpl);
